import React, { useState } from 'react';
import { AddUniversal } from '@duxton-web/icons';
import { Button, Flex, Popover, Space, Tooltip } from '@duxton-web/ui';
import { DefaultOptionType } from '@duxton-web/ui/es/select';

import AppVersionPrompt from './AppVersionPrompt';
import AppVersionTag from './AppVersionTag';
import { splitAppVersionString } from '@/common/AppVersionInput/appVersionUtils';

interface AppVersionProps {
  disabled?: boolean;
  value?: string;
  onChange?: (
    value: any,
    option: DefaultOptionType | DefaultOptionType[],
  ) => void;
}

function AppVersionValuesTags({
  disabled,
  value,
  showPopoverValue,
  onTagUpdate,
  onTagRemove,
  onShowPopover,
}) {
  const values = splitAppVersionString(value);

  if (!values?.length) return <>&nbsp;</>;

  return (
    <>
      {values?.map((val) => (
        <AppVersionTag
          disabled={disabled}
          value={val}
          key={val}
          showAppVersionPrompt={showPopoverValue === val}
          onChange={onTagUpdate}
          onRemove={onTagRemove}
          onShowPopover={onShowPopover}
        />
      ))}
    </>
  );
}

function AppVersionValues({
  disabled,
  value,
  showPopoverValue,
  onTagRemove,
  onTagUpdate,
  onShowPopover,
}) {
  const values = splitAppVersionString(value);

  if (!values?.length) return <></>;

  return (
    <Flex
      data-testid="app-version-wrapper"
      align="center"
      wrap="wrap"
      gap={4}
      style={{
        height: 40,
        border: '1px solid #bfbfbf',
        background: '#FFF',
        padding: '0 4px 0 4px',
        borderTopLeftRadius: 12,
        borderBottomLeftRadius: 12,
        borderRight: 0,
        minWidth: 100,
      }}
    >
      <AppVersionValuesTags
        disabled={disabled}
        value={value}
        showPopoverValue={showPopoverValue}
        onTagRemove={onTagRemove}
        onTagUpdate={onTagUpdate}
        onShowPopover={onShowPopover}
      />
    </Flex>
  );
}

function AddAppVersionButton({ value, disabled, onClick }) {
  const values = splitAppVersionString(value);

  return (
    <Tooltip title="Add a new version" placement="top">
      <Button
        data-testid="app-version-add"
        disabled={disabled}
        style={{
          borderTopRightRadius: 12,
          borderBottomRightRadius: 12,
          borderTopLeftRadius: values?.length ? 0 : 12,
          borderBottomLeftRadius: values?.length ? 0 : 12,
        }}
        icon={<AddUniversal />}
        onClick={onClick}
      />
    </Tooltip>
  );
}

export default function AppVersion({
  disabled,
  value = '',
  onChange = () => {},
}: AppVersionProps) {
  const [showPopoverValue, setShowPopoverValue] = useState<string>('');

  function onTagUpdate(val: string, prev: string) {
    let appVersions = splitAppVersionString(value);

    if (!value.includes(val)) {
      if (prev) {
        appVersions = appVersions.filter((ver) => prev !== ver);
      }

      onChange(
        [...appVersions, val].join(','),
        [...appVersions, val].map((inVal) => {
          return {
            value: inVal,
            label: inVal,
          };
        }),
      );
    }

    setShowPopoverValue('');
  }

  function onTagRemove(val: string) {
    const appVersions = splitAppVersionString(value);

    onChange(
      [...appVersions].filter((inVal) => inVal !== val).join(','),
      [...appVersions].filter((inVal) => {
        if (inVal !== val)
          return {
            value: inVal,
            label: inVal,
          };
      }),
    );
  }

  function onShowPopover(val: string) {
    setShowPopoverValue(val);
  }

  return (
    <Space.Compact>
      <AppVersionValues
        disabled={disabled}
        value={value}
        showPopoverValue={showPopoverValue}
        onTagUpdate={onTagUpdate}
        onTagRemove={onTagRemove}
        onShowPopover={onShowPopover}
      />
      <Popover
        placement="bottomLeft"
        open={showPopoverValue === 'add'}
        content={
          <AppVersionPrompt
            onSubmit={onTagUpdate}
            onCancel={() => {
              setShowPopoverValue('');
            }}
          />
        }
        trigger="click"
        destroyTooltipOnHide={true}
        onOpenChange={(open: boolean) => {
          if (open) setShowPopoverValue('add');
          else setShowPopoverValue('');
        }}
      >
        <AddAppVersionButton
          value={value}
          disabled={disabled}
          onClick={() => {
            setShowPopoverValue('add');
          }}
        />
      </Popover>
    </Space.Compact>
  );
}
